import React, { useEffect, useState } from 'react';
import type { ComponentPropsWithoutRef, ReactNode, SyntheticEvent } from 'react';

import PropTypes from 'prop-types';

import { FlagIcon } from 'apps/promo/components/common/FlagIcon/FlagIcon';
import { Icon } from 'apps/promo/components/common/Icon/Icon';
import { Text } from 'apps/promo/components/common/Text/Text';

import { DiskBadgeContainer, FlagIconContainer, FlagIconWrap, ImgWrap, SecondFlagIconWrap, type DiskBadgeSize, type DiskBadgeVariant } from './styles';

const getIconColor = (variant: DiskBadgeVariant | undefined, isDisabled?: boolean, isInactive?: boolean) => {
    switch (variant) {
        case 'primary':
            return 'white';
        case 'secondary':
            return isDisabled ? 'purpleRgba24' : isInactive ? 'purpleRgba40' : 'purple';
        case 'tertiary':
            return isDisabled ? 'lightGray' : isInactive ? 'mediumGray' : 'darkGray';
        case 'selectedPassive':
            return 'purple';
        case 'transparent':
            return 'purple';
        default:
            return 'darkGray';
    }
};

type ContainerProps = ComponentPropsWithoutRef<typeof DiskBadgeContainer>;

export interface DiskBadgeProps extends Omit<ContainerProps, 'children'> {
    iconName?: string;
    flagName?: string;
    secondFlagName?: string;
    diskText?: string;
    src?: string;
    alt?: string;
    logoSymbol?: ReactNode;
    withoutRadius?: boolean;
    onError?: (event: SyntheticEvent<HTMLImageElement, Event>) => void;
    variant?: DiskBadgeVariant;
    diskSize?: DiskBadgeSize;
}

export const DiskBadge = ({
    isDisabled,
    isInactive,
    variant = 'tertiary',
    diskSize = 'small',
    logoSymbol,
    iconName,
    src,
    alt,
    flagName,
    secondFlagName,
    diskText,
    withoutRadius,
    onError,
    ...rest
}: DiskBadgeProps) => {
    const [errorImageLoading, setErrorImageLoading] = useState(false);

    useEffect(() => {
        setErrorImageLoading(false);
    }, [src]);

    return (
        <DiskBadgeContainer
            variant={errorImageLoading ? 'tertiary' : variant}
            diskSize={diskSize}
            isDisabled={isDisabled}
            isInactive={isInactive}
            noBorder={!!src}
            {...rest}
        >
            {iconName && !errorImageLoading && (
                <Icon name={iconName} color={getIconColor(variant, isDisabled, isInactive)} size={diskSize === 'large' ? '48px' : '24px'} />
            )}
            {/*Загрузка аватарки*/}
            {src && !errorImageLoading && (
                <ImgWrap isDisabled={isDisabled} isInactive={isInactive} withoutRadius={withoutRadius} diskSize={diskSize} variant={variant}>
                    <img src={src} alt={alt} onError={(e) => (onError ? onError(e) : setErrorImageLoading(true))} />
                </ImgWrap>
            )}
            {/*Текст вместо иконки или аватарки*/}
            {diskText && (
                <Text size={diskSize === 'large' ? 'header' : 'main'} color={getIconColor(variant, isDisabled, isInactive)}>
                    {diskText}
                </Text>
            )}
            {/*обработка ошибок загрузки изображения*/}
            {errorImageLoading && (
                <Text size={diskSize === 'large' ? 'header' : 'main'} color={getIconColor(variant, isDisabled, isInactive)}>
                    {logoSymbol}
                </Text>
            )}
            {flagName && (
                <FlagIconWrap>
                    <FlagIconContainer isDisabled={isDisabled} isInactive={isInactive}>
                        <FlagIcon name={flagName} size={diskSize === 'large' ? 'x-small' : '16px'} />
                    </FlagIconContainer>
                </FlagIconWrap>
            )}
            {secondFlagName && (
                <SecondFlagIconWrap>
                    <FlagIconContainer isDisabled={isDisabled} isInactive={isInactive}>
                        <FlagIcon name={secondFlagName} size={diskSize === 'large' ? 'x-small' : '16px'} />
                    </FlagIconContainer>
                </SecondFlagIconWrap>
            )}
        </DiskBadgeContainer>
    );
};

DiskBadge.propTypes = {
    variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'letterIndex', 'selectedPassive', 'transparent', 'hasBorderedImg']),
    diskSize: PropTypes.oneOf(['x-small', 'small', 'fixed-small', 'large']),
    isDisabled: PropTypes.bool,
    isInactive: PropTypes.bool,
    iconName: PropTypes.string,
    flagName: PropTypes.string,
    secondFlagName: PropTypes.string,
    diskText: PropTypes.string,
    src: PropTypes.string,
    alt: PropTypes.string,
    logoSymbol: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    withoutRadius: PropTypes.bool,
    onError: PropTypes.func,
};
